import { useEffect, useRef, useState } from "react";
import { isAcceptedReportingTimeZone } from "../utils/reportingCalendar";

export const DASHBOARD_REPORTING_TIMEOUT_MS = 12000;
export const DASHBOARD_REPORTING_MAX_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [750, 2000];
const RETAIN_MS = 60000;
const requests = new Map();
const retained = new Map();

// Keep the deadline through body consumption. Navigation and deadlines are
// separate errors; neither can publish a late result into a newer scope.
export async function fetchDashboardReporting(url, { token, signal, fetcher = fetch } = {}) {
  const controller = new AbortController();
  let timer;
  let cancel;
  try {
    const guard = new Promise((_, reject) => {
      cancel = () => {
        const error = new Error("Dashboard request cancelled");
        error.name = "AbortError";
        controller.abort(error);
        reject(error);
      };
      if (signal?.aborted) return cancel();
      signal?.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(() => {
        const error = new Error("Dashboard reporting deadline exceeded");
        error.name = "TimeoutError";
        controller.abort(error);
        reject(error);
      }, DASHBOARD_REPORTING_TIMEOUT_MS);
    });
    const read = async () => {
      if (controller.signal.aborted) throw controller.signal.reason;
      const response = await fetcher(url, {
        headers: { Authorization: `Bearer ${token}`, "Cache-Control": "no-cache", Pragma: "no-cache" },
        cache: "no-store",
        signal: controller.signal,
      });
      if (controller.signal.aborted) throw controller.signal.reason;
      if (!response.ok) {
        const error = new Error(`Reporting HTTP ${response.status}`);
        error.status = response.status;
        const retryAfter = response.headers?.get?.("Retry-After");
        if (retryAfter) error.retryAfterMs = /^\d+(\.\d+)?$/.test(retryAfter)
          ? Number(retryAfter) * 1000 : Math.max(0, Date.parse(retryAfter) - Date.now());
        throw error;
      }
      return response.json();
    };
    return await Promise.race([guard, read()]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}

const isReportedNumber = (value) =>
  (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) && Number.isFinite(Number(value));

export function hasDashboardReporting(payload) {
  if (!payload || payload.reportingAvailability?.state === "unavailable") return false;
  const canonical = payload.canonicalSummary || payload.canonicalReporting;
  if (canonical?.source?.tier === "no_analytics_source" || canonical?.reportingAvailability?.state === "unavailable") return false;
  const values = [payload.metrics?.spendToDate, payload.metrics?.clientFacingSpend];
  if (values.some(isReportedNumber)) return true;
  return Object.values(canonical?.byChannel || {}).some((row) =>
    [row?.clientFacingSpend, row?.spend, row?.impressions, row?.clicks, row?.conversions, row?.videoViews].some(isReportedNumber));
}

const reportingScope = (payload) => {
  const canonical = payload?.canonicalSummary || payload?.canonicalReporting;
  return JSON.stringify([payload?.advertiser?.id, payload?.meta?.scopeKey,
    [...(canonical?.campaignScope?.activeCampaignIds || [])].map(String).sort(),
    canonical?.timeframe?.start, canonical?.timeframe?.end,
    payload?.meta?.timeZone || canonical?.timeframe?.timeZone, canonical?.spendBasis]);
};

export function isPartialDashboardReporting(payload) {
  const canonical = payload?.canonicalSummary || payload?.canonicalReporting;
  return payload?.reportingAvailability?.state === "partial" || canonical?.reportingAvailability?.state === "partial";
}

export function selectDashboardReporting(previous, incoming) {
  return previous && !isPartialDashboardReporting(previous) && isPartialDashboardReporting(incoming) &&
    reportingScope(previous) === reportingScope(incoming) ? previous : incoming;
}

// Coverage/freshness belongs next to the numbers, not in an error banner.
export function dashboardReportingCoverage(summary, isRetained = false) {
  if (!summary) return "";
  return [isPartialDashboardReporting(summary) ? "Partial coverage" : "",
    isRetained ? "Previously loaded" : ""].filter(Boolean).join(" · ");
}

// The reporting calendar is the backend's: the portal sends no zone, and
// accepts an answer only when it states an accepted reporting calendar (and
// states one calendar, not two).
export function dashboardReportingMatchesScope(payload, { advertiserId, campaignId, startDate, endDate } = {}) {
  if (String(payload?.advertiser?.id) !== String(advertiserId)) return false;
  const canonical = payload?.canonicalSummary || payload?.canonicalReporting || {};
  const timeframe = canonical?.timeframe || {};
  const returnedTimeZone = payload?.meta?.timeZone || timeframe?.timeZone || null;
  if (returnedTimeZone && !isAcceptedReportingTimeZone(returnedTimeZone)) return false;
  if (payload?.meta?.timeZone && timeframe?.timeZone && payload.meta.timeZone !== timeframe.timeZone) return false;
  if (campaignId != null) {
    const campaignIds = canonical?.campaignScope?.activeCampaignIds;
    if (!Array.isArray(campaignIds) || campaignIds.length !== 1 || String(campaignIds[0]) !== String(campaignId)) return false;
  }
  if (startDate && String(timeframe?.start || "") !== String(startDate)) return false;
  if (endDate && String(timeframe?.end || "") !== String(endDate)) return false;
  return true;
}

function waitForRetry(delay, signal) {
  return new Promise((resolve) => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener("abort", finish); resolve(); };
    const timer = setTimeout(finish, delay);
    signal.addEventListener("abort", finish, { once: true });
    if (signal.aborted) finish();
  });
}

function retryable(error) {
  return error.name !== "AbortError" && error.code !== "REPORTING_SCOPE" &&
    (!error.status || error.status === 408 || error.status === 429 || error.status >= 500);
}

function remember(key, summary, receivedAt) {
  retained.delete(key);
  retained.set(key, { summary, receivedAt });
  for (const [cachedKey, value] of retained) {
    if (Date.now() - value.receivedAt >= RETAIN_MS || retained.size > 8) retained.delete(cachedKey);
  }
}

function subscribeReporting(key, options, listener) {
  let entry = requests.get(key);
  if (!entry) {
    const cached = retained.get(key);
    const usable = cached && Date.now() - cached.receivedAt < RETAIN_MS;
    entry = { listeners: new Set(), controller: new AbortController(),
      state: { summary: usable ? cached.summary : null, receivedAt: usable ? cached.receivedAt : null,
        retained: Boolean(usable), loading: true, error: null, settledKey: "" } };
    requests.set(key, entry);
    const publish = (patch) => {
      if (entry.controller.signal.aborted) return;
      entry.state = { ...entry.state, ...patch };
      entry.listeners.forEach((notify) => notify(entry.state));
    };
    entry.run = async () => {
      const { signal } = entry.controller;
      const query = new URLSearchParams({ advertiserId: String(options.advertiserId) });
      if (options.campaignId != null) query.set("campaignId", String(options.campaignId));
      if (options.startDate) query.set("startDate", options.startDate);
      if (options.endDate) query.set("endDate", options.endDate);
      for (let attempt = 0; attempt < DASHBOARD_REPORTING_MAX_ATTEMPTS && !signal.aborted; attempt += 1) {
        let failure;
        let partialResponse = false;
        try {
          const payload = await fetchDashboardReporting(`${options.apiBase}/api/dashboard/welcome-summary?${query}`,
            { token: options.token, signal });
          if (signal.aborted) return;
          if (!dashboardReportingMatchesScope(payload, options)) {
            const error = new Error("Dashboard response scope mismatch");
            error.code = "REPORTING_SCOPE";
            throw error;
          }
          if (!hasDashboardReporting(payload)) throw new Error("No eligible dashboard reporting");
          const incoming = { ...payload, __welcomeSummaryRequestKey: options.requestKey };
          const summary = selectDashboardReporting(entry.state.summary, incoming);
          const held = summary !== incoming;
          const receivedAt = held ? entry.state.receivedAt : Date.now();
          remember(key, summary, receivedAt);
          publish({ summary, receivedAt, retained: held, error: null });
          if (!isPartialDashboardReporting(payload)) break;
          partialResponse = true;
          failure = new Error("Incomplete dashboard source coverage");
        } catch (error) {
          if (signal.aborted) return;
          failure = error;
          // Revoked authorization and mismatched scope must never retain money.
          if (error.status === 401 || error.status === 403 || error.code === "REPORTING_SCOPE") {
            retained.delete(key);
            publish({ summary: null, receivedAt: null, retained: false });
          }
        }
        const retryAfter = Number(failure?.retryAfterMs) || 0;
        const willRetry = attempt + 1 < DASHBOARD_REPORTING_MAX_ATTEMPTS && retryable(failure) && retryAfter <= 5000;
        console.warn("[dashboardReporting]", { attempt: attempt + 1, status: failure?.status || null,
          code: failure?.code || failure?.name, willRetry });
        publish({ error: failure, retained: partialResponse ? entry.state.retained : Boolean(entry.state.summary) });
        if (!willRetry) break;
        // Positive jitter disperses clients. Large Retry-After values end this
        // bounded cycle rather than retrying before the server permits it.
        await waitForRetry(Math.max(retryAfter, RETRY_DELAYS_MS[attempt] + Math.floor(Math.random() * 250)), signal);
      }
      publish({ loading: false, settledKey: options.requestKey });
    };
  }
  clearTimeout(entry.disposeTimer);
  entry.listeners.add(listener);
  listener(entry.state);
  if (entry.run) { const run = entry.run; entry.run = null; void run(); }
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size) return;
    // Share a request through StrictMode's cleanup/setup turn. A real unmount
    // cancels both transport and backoff before any further attempts.
    entry.disposeTimer = setTimeout(() => {
      if (entry.listeners.size) return;
      entry.controller.abort();
      if (requests.get(key) === entry) requests.delete(key);
    }, 0);
  };
}

export function useDashboardReporting({ requestKey, advertiserId, timeZone, token, apiBase, campaignId = null, startDate = null, endDate = null, onSuccess }) {
  // Full credential and API origin are in the memory-only identity; never log it.
  const scopeKey = JSON.stringify([requestKey, advertiserId, timeZone, campaignId, startDate, endDate, token, apiBase]);
  const enabled = Boolean(requestKey && token && advertiserId);
  const [state, setState] = useState({ key: "", summary: null, loading: false, error: null, settledKey: "" });
  const successRef = useRef(onSuccess);
  successRef.current = onSuccess;
  useEffect(() => {
    if (!enabled) return undefined;
    let accepted = null;
    return subscribeReporting(scopeKey, { requestKey, advertiserId, timeZone, token, apiBase, campaignId, startDate, endDate }, (next) => {
      setState({ ...next, key: scopeKey });
      if (next.summary && next.summary !== accepted) {
        accepted = next.summary;
        try { successRef.current?.(next.summary); }
        catch { console.warn("[dashboardReporting]", { code: "REPORTING_LOCAL_CACHE_WRITE" }); }
      }
    });
  }, [enabled, requestKey, advertiserId, timeZone, token, apiBase, scopeKey, campaignId, startDate, endDate]);
  // Clear at render, before an effect can publish another advertiser/filter.
  return enabled && state.key === scopeKey ? state :
    { summary: null, loading: enabled, error: null, settledKey: "", retained: false };
}

// A campaign filter belongs to the advertiser selected when that route was
// entered. Switching advertisers must not request the previous one's campaign.
export function useDashboardCampaignFilter(search, advertiserId) {
  const owner = useRef(null);
  if (Number.isFinite(advertiserId) && advertiserId > 0 && (!owner.current || owner.current.search !== search)) {
    owner.current = { search, advertiserId };
  }
  return owner.current?.advertiserId === advertiserId
    ? new URLSearchParams(search).get("campaignId") || null : null;
}
